/**
 * POS Hardware Devices admin — Yazıcı, ödeme terminali, handheld, KDS.
 *
 * Cihaz ekle, durum izle, test fişi yazdır, çekmece aç.
 */
import { Card, Stack, Table, Badge, Loader, Center, Tabs, Button, Group, ActionIcon, Indicator, Tooltip } from "@mantine/core";
import { IconPlus, IconEdit, IconTrash, IconPrinter, IconCash, IconRefresh, IconDeviceMobile, IconCreditCardPay, IconChefHat } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useResource, useRemove, useAction } from "../../../shared/api";
import { PageHeader, EmptyState, ResourceForm, confirmDelete, type FormField, formatDateTime } from "../../../shared/ui";

const FIELDS: FormField[] = [
  { name: "name", label: "Cihaz Adı", required: true, placeholder: "Kasa 1 - Yazıcı / Salon Handheld..." },
  { name: "device_type", label: "Tip", required: true, defaultValue: "PRINTER", placeholder: "PRINTER / PAYMENT_TERMINAL / HANDHELD / KDS_DISPLAY / KIOSK / CASH_DRAWER" },
  { name: "vendor", label: "Üretici", defaultValue: "GENERIC", placeholder: "SUNMI / PAX / VERIFONE / EPSON / STAR / GENERIC" },
  { name: "model_code", label: "Model Kodu", placeholder: "A920, V2, TSP143..." },
  { name: "serial_number", label: "Seri No" },
  { name: "connection_type", label: "Bağlantı Tipi", required: true, defaultValue: "TCP", placeholder: "TCP / USB / BLUETOOTH / SDK" },
  { name: "ip_address", label: "IP Adresi (TCP için)" },
  { name: "port", label: "Port", type: "number", defaultValue: 9100 },
  { name: "bluetooth_address", label: "Bluetooth MAC" },
  { name: "usb_path", label: "USB Path" },
  { name: "is_fiscal_ynokc", label: "🇹🇷 YN ÖKC Yazarkasa-POS (GİB onaylı mali fiş basar)", type: "switch", defaultValue: false },
  { name: "fiscal_serial_no", label: "Mali Seri No (cihaz etiketinde)", placeholder: "PX0001234567" },
  { name: "fiscal_brand_model", label: "GİB Marka/Model", placeholder: "PAX A920 TR" },
  { name: "fiscal_tsm_provider", label: "TSM/Onaylı Sağlayıcı", placeholder: "BKM, Asseco, Hugin..." },
  { name: "fiscal_certified_until", label: "Sertifika Bitiş (YYYY-MM-DD)", placeholder: "2027-12-31" },
  { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
];

const STATUS_COLOR: Record<string, string> = {
  ONLINE: "green",
  OFFLINE: "gray",
  ERROR: "red",
  UNKNOWN: "yellow",
};

const TYPE_ICON: Record<string, any> = {
  PRINTER: IconPrinter,
  PAYMENT_TERMINAL: IconCreditCardPay,
  KDS_DISPLAY: IconChefHat,
  HANDHELD: IconDeviceMobile,
  KIOSK: IconDeviceMobile,
  CASH_DRAWER: IconCash,
};

const TYPE_LABEL: Record<string, string> = {
  PRINTER: "Yazıcı",
  PAYMENT_TERMINAL: "Ödeme Terminali",
  KDS_DISPLAY: "Mutfak Ekranı",
  HANDHELD: "Garson Handheld",
  KIOSK: "Self-Service Kiosk",
  CASH_DRAWER: "Para Çekmecesi",
};

export function DevicesPage() {
  return (
    <Stack gap="md">
      <PageHeader title="POS Donanım Cihazları" subtitle="Yazıcı, ödeme terminali, handheld, mutfak ekranı yönetimi" />
      <Tabs defaultValue="devices">
        <Tabs.List>
          <Tabs.Tab value="devices">Cihazlar</Tabs.Tab>
          <Tabs.Tab value="print-jobs">Yazdırma Kuyruğu</Tabs.Tab>
          <Tabs.Tab value="terminal-tx">Terminal İşlemleri</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="devices" pt="md">
          <DeviceList />
        </Tabs.Panel>
        <Tabs.Panel value="print-jobs" pt="md">
          <PrintJobList />
        </Tabs.Panel>
        <Tabs.Panel value="terminal-tx" pt="md">
          <TerminalTransactionList />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function DeviceList() {
  const { data, isLoading, refetch } = useResource<any>("hardware-devices", { refetchInterval: 30000 });
  const remove = useRemove("hardware-devices");
  const printTest = useAction("hardware-devices", "print-test");
  const openDrawer = useAction("hardware-devices", "open-drawer");

  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);

  const list: any[] = data?.results || data || [];
  if (isLoading) return <Center h={200}><Loader /></Center>;

  async function handleDelete(id: string) {
    if (!(await confirmDelete("Cihaz silinsin mi?"))) return;
    await remove.mutateAsync(id);
    notifications.show({ message: "Silindi", color: "green" });
  }

  async function handleTest(id: string) {
    try {
      await printTest.mutateAsync({ id });
      notifications.show({ message: "Test baskısı gönderildi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  async function handleDrawer(id: string) {
    try {
      await openDrawer.mutateAsync({ id });
      notifications.show({ message: "Çekmece komutu gönderildi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Group>
          <Button leftSection={<IconRefresh size={16} />} variant="default" onClick={() => refetch()}>Yenile</Button>
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); open(); }}>Yeni Cihaz</Button>
      </Group>

      {list.length === 0 ? (
        <EmptyState
          title="Cihaz yok"
          description="Yazıcı, POS terminali, handheld gibi donanımları ekleyin. Pilot için Sunmi V2 + Star TSP143 yeterli."
        />
      ) : (
        <Card padding="sm" radius="md" withBorder>
          <Table.ScrollContainer minWidth={820}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th></Table.Th>
                <Table.Th>Ad</Table.Th>
                <Table.Th>Tip</Table.Th>
                <Table.Th>Üretici / Model</Table.Th>
                <Table.Th>Bağlantı</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th>Son Heartbeat</Table.Th>
                <Table.Th>İşlem</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.map((d: any) => {
                const Icon = TYPE_ICON[d.device_type] || IconPrinter;
                return (
                  <Table.Tr key={d.id}>
                    <Table.Td>
                      <Indicator color={STATUS_COLOR[d.status] || "gray"} size={10} processing={d.status === "ONLINE"}>
                        <Icon size={22} />
                      </Indicator>
                    </Table.Td>
                    <Table.Td fw={500}>{d.name}</Table.Td>
                    <Table.Td><Badge variant="light">{TYPE_LABEL[d.device_type] || d.device_type}</Badge></Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Badge size="xs">{d.vendor}</Badge>
                        {d.model_code && <code style={{ fontSize: 11 }}>{d.model_code}</code>}
                        {d.is_fiscal_ynokc && (
                          <Tooltip label={`YN ÖKC Mali Cihaz — Seri: ${d.fiscal_serial_no}`}>
                            <Badge size="xs" color="green" variant="filled">🇹🇷 MALİ</Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <span style={{ fontSize: 11 }}>
                        {d.connection_type === "TCP" ? `${d.ip_address || "?"}:${d.port}` : d.connection_type}
                      </span>
                    </Table.Td>
                    <Table.Td><Badge color={STATUS_COLOR[d.status] || "gray"}>{d.status}</Badge></Table.Td>
                    <Table.Td>{d.last_heartbeat_at ? formatDateTime(d.last_heartbeat_at) : "-"}</Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {d.device_type === "PRINTER" && (
                          <Tooltip label="Test Baskısı">
                            <ActionIcon variant="subtle" color="blue" onClick={() => handleTest(d.id)}>
                              <IconPrinter size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        {d.device_type === "CASH_DRAWER" && (
                          <Tooltip label="Çekmeceyi Aç">
                            <ActionIcon variant="subtle" color="orange" onClick={() => handleDrawer(d.id)}>
                              <IconCash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                        <ActionIcon variant="subtle" onClick={() => { setEditing(d); open(); }}><IconEdit size={16} /></ActionIcon>
                        <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(d.id)}><IconTrash size={16} /></ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="hardware-devices"
        title={editing ? "Cihaz Düzenle" : "Yeni Cihaz"}
        fields={FIELDS}
        editId={editing?.id || null}
        initialValues={editing || undefined}
      />
    </Stack>
  );
}

// Aktif (PENDING/PRINTING) job varsa 5 sn'de bir kontrol et — yazıcı kuyruğu
// kullanıcı izlerken hızlı ilerlesin. Hepsi terminal state ise (SUCCESS /
// FAILED / CANCELLED) polling'i 60 sn'ye düşür: liste değişmez, sadece yeni
// job eklenirse görmek için yavaş bir nabız yeter.
function PrintJobList() {
  const { data, isLoading } = useResource<any>("print-jobs", {
    refetchInterval: (query) => {
      const list: any[] = query.state.data?.results || query.state.data || [];
      const hasActive = list.some(
        (j: any) => j.status === "PENDING" || j.status === "PRINTING",
      );
      return hasActive ? 5000 : 60000;
    },
  });
  const list: any[] = data?.results || data || [];
  if (isLoading) return <Center h={200}><Loader /></Center>;
  if (list.length === 0) return <EmptyState title="Yazdırma kuyruğu boş" />;

  const JOB_COLOR: Record<string, string> = {
    PENDING: "yellow", PRINTING: "blue", SUCCESS: "green", FAILED: "red", CANCELLED: "gray",
  };

  return (
    <Card padding="sm" radius="md" withBorder>
      <Table.ScrollContainer minWidth={640}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Zaman</Table.Th>
            <Table.Th>Cihaz</Table.Th>
            <Table.Th>Tip</Table.Th>
            <Table.Th>Durum</Table.Th>
            <Table.Th>Deneme</Table.Th>
            <Table.Th>Hata</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {list.slice(0, 100).map((j: any) => (
            <Table.Tr key={j.id}>
              <Table.Td>{formatDateTime(j.created_at)}</Table.Td>
              <Table.Td>{j.device_name}</Table.Td>
              <Table.Td><Badge variant="light">{j.job_type}</Badge></Table.Td>
              <Table.Td><Badge color={JOB_COLOR[j.status]}>{j.status}</Badge></Table.Td>
              <Table.Td>{j.attempts}</Table.Td>
              <Table.Td><span style={{ fontSize: 11, color: "var(--mantine-color-red-7)" }}>{j.last_error?.substring(0, 60) || "-"}</span></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
    </Card>
  );
}

function TerminalTransactionList() {
  const { data, isLoading } = useResource<any>("terminal-transactions");
  const list: any[] = data?.results || data || [];
  if (isLoading) return <Center h={200}><Loader /></Center>;
  if (list.length === 0) return <EmptyState title="Terminal işlemi yok" description="POS terminali ile yapılan kart ödemeleri burada listelenir." />;

  const TX_COLOR: Record<string, string> = {
    APPROVED: "green", DECLINED: "red", CANCELLED: "gray", ERROR: "red", TIMEOUT: "orange",
    INITIATED: "blue", PROCESSING: "blue",
  };

  return (
    <Card padding="sm" radius="md" withBorder>
      <Table.ScrollContainer minWidth={720}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Zaman</Table.Th>
            <Table.Th>Terminal</Table.Th>
            <Table.Th>Kart</Table.Th>
            <Table.Th>Auth Kodu</Table.Th>
            <Table.Th>Taksit</Table.Th>
            <Table.Th ta="right">Tutar</Table.Th>
            <Table.Th>Durum</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {list.slice(0, 100).map((t: any) => (
            <Table.Tr key={t.id}>
              <Table.Td>{formatDateTime(t.created_at)}</Table.Td>
              <Table.Td>{t.device_name}</Table.Td>
              <Table.Td>{t.card_type} **** {t.card_last_4 || "----"}</Table.Td>
              <Table.Td><code style={{ fontSize: 11 }}>{t.auth_code || "-"}</code></Table.Td>
              <Table.Td>{t.installment_count}x</Table.Td>
              <Table.Td ta="right" fw={600}>{(t.amount_minor / 100).toFixed(2)} TL</Table.Td>
              <Table.Td><Badge color={TX_COLOR[t.status]}>{t.status}</Badge></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
    </Card>
  );
}
